import React, { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import CodeInput from "./CodeInput";
import RecoveryCodes from "./RecoveryCodes";
import { mfaError, mfaSetupConfirm, mfaSetupStart } from "utils/mfaApi";

// Set up an authenticator app: scan, confirm with a code, keep the recovery
// codes. `token` is the half-finished login's token, or nothing when the
// person is already signed in (the request then uses their session).
//
// Turning it on ends every session, so `onTokens` gets the fresh ones the
// moment the code is accepted, before the recovery codes are even shown.
const MfaEnrol = ({ token, startBody, onTokens, onDone, onCancel, intro }) => {
  const [step, setStep] = useState("loading");
  const [setup, setSetup] = useState(null);
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    // Once only: a second start would replace the secret the first QR shows.
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const res = await mfaSetupStart(token, startBody);
        const data = res.data.result;
        setSetup(data);
        setQr(await QRCode.toDataURL(data.otpauthUri, { width: 200, margin: 1 }));
        setStep("scan");
      } catch (e) {
        setError(mfaError(e));
        setStep("failed");
      }
    })();
  }, [token, startBody]);

  const confirm = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return setError("Enter the 6-digit code from your app.");
    setBusy(true);
    setError("");
    try {
      const res = await mfaSetupConfirm(token, { code });
      const data = res.data.result;
      setResult(data);
      if (onTokens) onTokens({ token: data.token, refreshToken: data.refreshToken });
      setStep("codes");
    } catch (err) {
      setError(mfaError(err, "That code didn't work. Try again."));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  if (step === "codes") {
    return (
      <RecoveryCodes
        codes={result.recoveryCodes}
        onContinue={() => onDone({ token: result.token, refreshToken: result.refreshToken })}
      />
    );
  }

  if (step === "failed") {
    return (
      <div className="mfa-card">
        <p className="mfa-error">{error}</p>
        {onCancel && (
          <div className="mfa-actions">
            <button type="button" className="mfa-secondary-btn" onClick={onCancel}>
              Back
            </button>
          </div>
        )}
      </div>
    );
  }

  if (step === "loading") return <div className="mfa-card">Getting ready…</div>;

  return (
    <form className="mfa-card" onSubmit={confirm}>
      {intro && <p className="mfa-lead">{intro}</p>}
      <ol className="mfa-steps">
        <li>Open an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…).</li>
        <li>Scan this code, or enter the key by hand.</li>
        <li>Type the 6-digit code the app shows.</li>
      </ol>
      <img className="mfa-qr" src={qr} alt="QR code for your authenticator app" />
      <code className="mfa-key">{setup.secret}</code>
      <CodeInput value={code} onChange={setCode} />
      {error && <p className="mfa-error">{error}</p>}
      <div className="mfa-actions">
        <button type="submit" className="mfa-primary-btn" disabled={busy}>
          Turn on
        </button>
        {onCancel && (
          <button type="button" className="mfa-link" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

export default MfaEnrol;
